import { useCrmLocale } from '../ui/locale/crmLocale';
import React from 'react';
import { useToast } from '../ui/hooks';
import { useAuth } from '../auth/useAuth';
import { ensureLogoutNavigationPurge } from '../auth/logoutBoundary';
import { purgePrivateCaches } from '../pwa/offlineCacheClient';

/**
 * Mount once near the root. Listens for the global 'app:logout' custom event
 * (emitted by authFetch or realtime layer) and surfaces a consistent toast.
 */
const AutoLogoutListener: React.FC = () => {
  const { clearAuth } = useAuth();
  const { t: crmText } = useCrmLocale();
  const { push } = useToast();
  React.useEffect(()=>{
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      // External logout events purge here when the root cache listener has not run first.
      try {
        const proof = ensureLogoutNavigationPurge(e);
        clearAuth(proof);
      } catch {/* ignore */}
      void purgePrivateCaches();
      push?.(detail?.reason === 'unauthorized' ? crmText('Session expired. Please log in again.') : crmText('You have been logged out'), 'warning');
    };
    window.addEventListener('app:logout', handler as EventListener);
    return ()=> window.removeEventListener('app:logout', handler as EventListener);
  }, [clearAuth, push, crmText]);
  return null;
};

export default AutoLogoutListener;
